import { BehaviorSubject, Observable } from 'rxjs';
import { clamp } from '@immosky/shared';

export interface PaginatorLayoutInfo {
  visiblePageLinks: number[];
  displayLeftSeparator: boolean;
  displayRightSeparator: boolean;
}

export class PaginatorLayoutRenderer {
  private readonly PAGE_LINK_WIDTH = 38;
  private readonly SEPARATOR_WIDTH = 12;

  private isDestroyed = false;
  private changeDetected = false;

  private readonly containerElement!: HTMLElement;
  private currentContainerWidth = 0;

  private pageCount = 0;
  private currentPageIndex = 0;

  private visibleCount = 0;
  private maxStartIndex = 0;
  private sideSize = 0;
  private startIndex = 0;
  private endIndex = 0;

  private layoutInformation$ = new BehaviorSubject<PaginatorLayoutInfo>({
    visiblePageLinks: [],
    displayLeftSeparator: false,
    displayRightSeparator: false,
  });

  constructor(containerElement: HTMLElement) {
    this.containerElement = containerElement;
  }

  start() {
    window.requestAnimationFrame(this.loop.bind(this));
  }

  private loop() {
    this.updateContainerWidth();

    if (this.changeDetected) {
      this.updateLayout();
      this.changeDetected = false;
    }

    if (!this.isDestroyed) {
      window.requestAnimationFrame(this.loop.bind(this));
    }
  }

  private updateContainerWidth() {
    const containerRect = this.containerElement.getBoundingClientRect();
    const previousContainerWidth = this.currentContainerWidth;

    if (previousContainerWidth !== containerRect.width) {
      this.currentContainerWidth = containerRect.width;
      this.changeDetected = true;
    }
  }

  private updateLayout() {
    const visiblePageLinks = [];
    let displayLeftSeparator = false;
    let displayRightSeparator = false;

    this.determineVisiblePageLinkCount();

    // if all pages can be displayed
    if (this.visibleCount >= this.pageCount) {
      this.startIndex = 0;
      this.endIndex = this.pageCount - 1;
    } else {
      this.recalculateIndexes();

      displayLeftSeparator = this.startIndex > 0;
      displayRightSeparator = this.endIndex < this.pageCount - 1;

      if (displayLeftSeparator || displayRightSeparator) {
        this.visibleCount -= 2;
        this.recalculateIndexes();
      }

      if (!displayLeftSeparator) {
        this.startIndex = 0;
        this.endIndex = this.visibleCount;
      }

      if (!displayRightSeparator) {
        this.endIndex = this.pageCount - 1;
        this.startIndex = this.pageCount - this.visibleCount - 1;
      }
    }

    // create link indexes
    for (let i = this.startIndex; i <= this.endIndex; i++) {
      visiblePageLinks.push(i);
    }

    // notify layout change
    this.layoutInformation$.next({
      visiblePageLinks,
      displayLeftSeparator,
      displayRightSeparator,
    });
  }

  private determineVisiblePageLinkCount() {
    let count = Math.floor(
      (this.currentContainerWidth - 2 * this.SEPARATOR_WIDTH) /
        this.PAGE_LINK_WIDTH
    );
    if (count % 2 === 0) {
      count--;
    }
    this.visibleCount = count;
  }

  private recalculateIndexes() {
    this.sideSize = (this.visibleCount - 1) * 0.5;

    this.maxStartIndex = this.pageCount - this.visibleCount;
    this.startIndex = clamp(
      0,
      this.currentPageIndex - this.sideSize,
      this.maxStartIndex
    );

    this.endIndex = Math.min(
      this.pageCount - 1,
      this.startIndex + this.visibleCount - 1
    );
  }

  destroy() {
    this.isDestroyed = true;
  }

  setPageCount(pageCount: number) {
    const previousPageCount = this.pageCount;

    if (previousPageCount !== pageCount) {
      this.pageCount = pageCount;
      this.changeDetected = true;
    }
  }

  setCurrentPageIndex(index: number) {
    const previousIndex = this.currentPageIndex;

    if (previousIndex !== index) {
      this.currentPageIndex = index;
      this.changeDetected = true;
    }
  }

  get layoutInformation(): Observable<PaginatorLayoutInfo> {
    return this.layoutInformation$.asObservable();
  }
}
